'use client';

import { Collapse, freddyTheme, Typography } from '@coolblue-development/freddy';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { useTranslation } from '@i18n/useTranslation';
import { useEffect, useRef, useState } from 'react';

type Props = { text: string; expanded: boolean; expand: (collapsed: boolean) => void };

const Text = ({ text, expanded, expand }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [shouldWrapInCollapse, setShouldWrapInCollapse] = useState(false);
  const { t } = useTranslation('webshop.productPage');
  const collapseTranslations = useFreddyTranslations('collapse');

  const formatText = (text: string) => {
    if (text === 'true') {
      return t('yes');
    } else if (text === 'false') {
      return t('no');
    }
    return text;
  };

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      const scrollHeight = entry.target.scrollHeight;
      const heightOfTwoLines = parseFloat(getComputedStyle(entry.target).lineHeight) * 2;

      const shouldCollapse = scrollHeight > heightOfTwoLines;

      setShouldWrapInCollapse(shouldCollapse);
    });

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [text]);

  const content = <Typography ref={ref}>{formatText(text)}</Typography>;

  return shouldWrapInCollapse ? (
    <Collapse
      open={expanded}
      onToggle={() => expand(!expanded)}
      collapsedLines={2}
      sx={{
        button: { paddingTop: `${freddyTheme.spacing[100]}` }
      }}
      translations={collapseTranslations}
    >
      {content}
    </Collapse>
  ) : (
    content
  );
};

export default Text;
